import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { SignupReason } from './SignupCardV2';
import { Cloud, Edition, countriesKeyedByIsoCode, isCortexCodeFlow } from '@signup/shared';
import { parseQueryParams } from '../../../../utils/ParseQueryParams';
import * as configs from '../../../../utils/configurations';
import { useSignupPageContext } from '../../../../pages/SignupContext';

// Define the shape of the context state
export interface SignupFormDataState {
  firstName: string;
  lastName: string;
  email: string;
  countryIsoCode: string;
  signupReason: SignupReason | null;
  schoolName: string;
  companyName: string;
  jobTitle: string;
  edition: Edition;
  cloud: Cloud;
  region: string;
  termsAgreement: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setField: (field: SignupFormFields, value: any) => void;
  optInEmailAgreement: boolean;
  optOutEmailAgreement: boolean;
  givenEmailOptInChoice: boolean;
  phoneNumber: string;
  filterVisibleRegions: boolean;
}

export type SignupFormFields = Exclude<keyof SignupFormDataState, 'setField'>;

// Create the context
export const SignupFormDataContext = createContext<SignupFormDataState | undefined>(undefined);

// Create the provider component
const SignupFormDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { modifiedSignupFlowExperimentGroup } = useSignupPageContext();

  const [formState, setFormState] = useState(() => {
    const [signupParams] = parseQueryParams(window.location.search);
    const firstName = signupParams.firstname || '';
    const lastName = signupParams.lastname || '';
    const email = signupParams.email || '';
    const signupFlowType = configs.getSignupFlowTypeWithoutListener();
    const defaultEdition = isCortexCodeFlow(signupFlowType) ? Edition.STANDARD : Edition.ENTERPRISE;
    const edition = (signupParams.plan || signupParams.planp || defaultEdition) as Edition;
    const cloud = (signupParams.cloud || signupParams.cloudp || Cloud.AWS) as Cloud;
    const region = signupParams.region || signupParams.regionp || '';
    const ipIsoCode = configs.getIsoCode();
    const countryIsoCode = ipIsoCode && countriesKeyedByIsoCode[ipIsoCode] ? ipIsoCode : '';
    const isModifiedFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

    return {
      firstName,
      lastName,
      email,
      countryIsoCode,
      signupReason: null,
      schoolName: '',
      companyName: '',
      phoneNumber: '',
      jobTitle: '',
      edition,
      cloud,
      region,
      termsAgreement: isModifiedFlow ? true : false,
      optInEmailAgreement: false,
      optOutEmailAgreement: false,
      givenEmailOptInChoice: false,
      filterVisibleRegions: true,
    };
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setField = useCallback((field: SignupFormFields, value: any) => {
    setFormState(prevState => ({
      ...prevState,
      [field]: value,
    }));
  }, []);

  return (
    <SignupFormDataContext.Provider value={{ ...formState, setField }}>
      {children}
    </SignupFormDataContext.Provider>
  );
};

// Custom hook for using the context
const useSignupFormData = () => {
  const context = useContext(SignupFormDataContext);
  if (context === undefined) {
    throw new Error('useSignupFormData must be used within a SignupFormDataProvider');
  }
  return context;
};

// Non-throwing variant for components that may render outside the provider
// (e.g. SocialLoginButtons when used outside the modified signup form).
const useSignupFormDataOptional = (): SignupFormDataState | undefined => {
  return useContext(SignupFormDataContext);
};

export { SignupFormDataProvider, useSignupFormData, useSignupFormDataOptional };
